import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getRequest, patchRequest } from 'services/apiRequests';
import { CSRFService } from 'services/csrf';
import type { APIList, StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';
import type { ProposedChangeWorksheetRow } from 'components/Collaboration/utils/collaboration-types';
import { QueryHookOptions } from 'types/ReactQuery';

type SPMProposedChangeRowsParams = {
  changeGroupId: StringifiedUUID;
  worksheetId?: StringifiedUUID;
};

const SPMProposedChangeRowsUrl = (params: SPMProposedChangeRowsParams) => {
  const base = `/api/v1/spm/change-groups/${params.changeGroupId}/proposed-change-rows/`;
  if (params.worksheetId) {
    return `${base}?worksheet_id=${params.worksheetId}`;
  }
  return base;
};

/** Query key index 1. Exported so callers can match the key without duplicating the literal. */
export const PROPOSED_CHANGE_ROWS_KEY_SEGMENT = 'proposed-change-rows';

const getSPMProposedChangeRowsQueryKey = (params: Partial<SPMProposedChangeRowsParams>) =>
  ['spm', PROPOSED_CHANGE_ROWS_KEY_SEGMENT, params.changeGroupId].filter((item): item is string => Boolean(item));

const listSPMProposedChangeRows = (params: SPMProposedChangeRowsParams) =>
  getRequest<null, APIList<ProposedChangeWorksheetRow>>(SPMProposedChangeRowsUrl(params));

type UseSPMProposedChangeRowsQuery = {
  changeGroupId: StringifiedUUID | undefined;
  worksheetId?: StringifiedUUID;
  options?: QueryHookOptions<APIList<ProposedChangeWorksheetRow>, APIError>;
};

const useSPMProposedChangeRowsQuery = ({
  changeGroupId,
  worksheetId,
  options = { enabled: true },
}: UseSPMProposedChangeRowsQuery) => {
  const QUERY_KEY = getSPMProposedChangeRowsQueryKey({ changeGroupId });

  return useQuery<APIList<ProposedChangeWorksheetRow>, APIError>({
    ...options,
    enabled: options.enabled && !!changeGroupId,
    queryKey: QUERY_KEY,
    queryFn: () => listSPMProposedChangeRows({ changeGroupId: changeGroupId as string, worksheetId }),
  });
};

const reviewStatusUrl = '/api/v1/collaboration/proposed-change-rows/review-status/';

type ReviewStatusUpdateRequest = {
  row_ids: string[];
  review_status: 'rejected' | null;
};

type ReviewStatusUpdateResponse = {
  updated_count: number;
};

const updateReviewStatus = (csrfToken: string, data: ReviewStatusUpdateRequest) =>
  patchRequest<ReviewStatusUpdateRequest, ReviewStatusUpdateResponse>(reviewStatusUrl, data, csrfToken);

const useUpdateReviewStatusMutation = (changeGroupId: StringifiedUUID | undefined) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';
  const queryClient = useQueryClient();

  return useMutation<ReviewStatusUpdateResponse, APIError, ReviewStatusUpdateRequest>({
    mutationFn: (data) => updateReviewStatus(csrfToken, data),
    onSuccess: () => {
      if (changeGroupId) {
        queryClient.invalidateQueries({ queryKey: getSPMProposedChangeRowsQueryKey({ changeGroupId }) });
      }
    },
  });
};

export const ProposedChangeGroupsServiceV2 = {
  useSPMProposedChangeRowsQuery,
  getSPMProposedChangeRowsQueryKey,
  useUpdateReviewStatusMutation,
};
